import { useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import type { TFunction } from 'i18next';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from 'components/ui/drawer';
import { TIME_WINDOWS, isOutsideWindow, windowHours, windowLabel } from 'constants/timeWindows';
import { formatDay } from 'utils/formatDate';
import {
  addDays,
  calendarDay,
  isDeadlinePassed,
  parisNowTime,
  parisToday,
  shortDay,
  weekdayDay,
} from 'utils/interventionDate';
import type { AcceptSlot, MyInterventionRequest } from 'api/myInterventionRequestsApi';
import DayGrid, { fitsGrid } from './DayGrid';
import { PICKED } from './requestVisuals';

/**
 * The acceptance sheet (E3, E12): he picks his day within [max(today, earliest), deadline] and his
 * time, both at Paris time (CA18) — a grid, pills past six weeks, a lock card on a fixed day (F14).
 * A slot the server would refuse is refused here, with no network call (CA3); an hour outside the
 * window only warns. Same three-pane layout as `DeclineSheet` (vaul trap).
 */

const LABEL = 'text-[13px] font-medium text-zinc-900 dark:text-zinc-100';
const PILL =
  'min-h-11 rounded-md border px-3 text-[14px] disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const PILL_OFF = 'border-zinc-200 text-zinc-700 dark:border-zinc-800 dark:text-zinc-300';

/** A `switch` of literal keys — a key built by concatenation is invisible to the parity check. */
const pillLabel = (t: TFunction, offset: number): string => {
  switch (offset) {
    case 0:
      return t('Today');
    case 1:
      return t('Tomorrow');
    default:
      return t('The day after tomorrow');
  }
};

interface Form {
  day: string;
  time: string;
  otherDay: boolean;
}

type Action =
  | { type: 'PICK_DAY'; day: string }
  | { type: 'OTHER_DAY' }
  | { type: 'SET_DAY'; day: string }
  | { type: 'SET_TIME'; time: string };

const formReducer = (state: Form, action: Action): Form => {
  switch (action.type) {
    case 'PICK_DAY':
      return { ...state, day: action.day, otherDay: false };
    case 'OTHER_DAY':
      return { ...state, day: '', otherDay: true };
    case 'SET_DAY':
      return { ...state, day: action.day };
    default:
      return { ...state, time: action.time };
  }
};

/** Prefilled with the indicative time, else the window's start, else empty (E10). */
const prefilledTime = (request: MyInterventionRequest): string =>
  request.indicativeTime ?? (request.timeWindow ? TIME_WINDOWS[request.timeWindow]?.from : null) ?? '';

/** The refusal the server would give, worded as it would (CA3); `null` when the slot is sendable. */
const slotRefusal = (t: TFunction, form: Form, deadline: string | null, earliest: string | null): string | null => {
  if (!form.day || !form.time) return null;
  const today = parisToday();
  if (deadline && form.day > deadline) return t('This day is after the deadline. Pick an earlier day.');
  if (earliest && earliest > today && form.day < earliest) {
    return t('No visit before {{start}}', { start: shortDay(earliest) });
  }
  if (form.day < today || (form.day === today && form.time < parisNowTime())) {
    return t('This day or time has already passed. Pick a later slot.');
  }
  return null;
};

interface AcceptFormProps {
  request: MyInterventionRequest;
  pending: boolean;
  /** Already a sentence: the caller owns the families of failure, this only renders one. */
  error: string | null;
  /** The network failure: the confirm button reads « Réessayer ». */
  retryable: boolean;
  /** The race is lost (CA4): no retry, only the way back. */
  taken: boolean;
  /** « 180 € HT », or null when the mission carries no tariff. */
  tariffText: string | null;
  /** « 40 € HT », this technician's live travel fee, or null. */
  feeText: string | null;
  /** The slot changed: a failure about the previous one no longer applies. */
  onEdit: () => void;
  /** His other accepted missions that day (D68) — informs, never blocks. */
  sameDayMissions: (day: string) => number;
  onClose: () => void;
  onBackToList: () => void;
  onConfirm: (slot: AcceptSlot) => void;
}

/** The part holding the form state — keyed per opening by `AcceptSheet`. */
const AcceptForm = ({
  request,
  pending,
  error,
  retryable,
  taken,
  tariffText,
  feeText,
  sameDayMissions,
  onEdit,
  onClose,
  onBackToList,
  onConfirm,
}: AcceptFormProps) => {
  const { t } = useTranslation();
  const today = parisToday();
  const deadline = request.deadlineDate ?? null;
  const earliest = calendarDay(request.earliestDate);
  const start = earliest && earliest > today ? earliest : today;
  // A fixed day (earliest = deadline) is chosen already: only the time remains, none imposed (G5).
  const fixedDay = !!earliest && earliest === deadline;
  const [form, dispatchForm] = useReducer(formReducer, request, (initial) => ({
    day: fixedDay ? (deadline ?? '') : '',
    time: prefilledTime(initial),
    otherDay: false,
  }));
  const dispatch = (action: Action) => {
    onEdit();
    dispatchForm(action);
  };

  const deadlinePassed = isDeadlinePassed(deadline, today);
  // Pre-L7 rows (no earliest) keep the L6 pills; so does a window too long for the grid.
  const grid = !!earliest && !!deadline && !fixedDay && !deadlinePassed && fitsGrid(start, deadline);
  const pills = [0, 1, 2].filter((offset) => {
    const day = addDays(today, offset);
    return day >= start && (!deadline || day <= deadline);
  });
  const hours = windowHours(request.timeWindow);
  const label = windowLabel(t, request.timeWindow);
  const refusal = deadlinePassed
    ? t('The deadline has passed: contact the GPS team.')
    : slotRefusal(t, form, deadline, earliest);
  const load = form.day ? sameDayMissions(form.day) : 0;
  const canConfirm = !!form.day && !!form.time && !refusal && !pending;

  const submit = () => {
    // Re-checked on the click: the clock may have moved since the last render.
    if (!canConfirm || slotRefusal(t, form, deadline, earliest)) return;
    onConfirm({ chosenDate: form.day, chosenTime: form.time });
  };

  const confirmLabel = () => {
    if (pending) return t('Sending your answer…');
    if (retryable) return t('Retry');
    if (form.day && form.time) return t('Confirm · {{day}} at {{time}}', { day: formatDay(form.day), time: form.time });
    return t('Confirm');
  };

  return (
    <>
      {/* The only scrolling pane: vaul owns every touch on `DrawerContent` itself. */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 pb-4">
        <div className="flex flex-col gap-2" role="group" aria-labelledby="accept-day">
          <p id="accept-day" className={LABEL}>
            {t('Day of the visit')}
          </p>
          {fixedDay && deadline ? (
            <div className="flex gap-2.5 rounded-md border border-zinc-200 p-3 dark:border-zinc-800">
              <Lock className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400" aria-hidden="true" />
              <div>
                <p className="text-[14px] font-medium text-zinc-900 dark:text-zinc-50">
                  {t('Fixed day: {{day}}', { day: weekdayDay(deadline) })}
                </p>
                <p className="text-[12.5px] text-zinc-500 dark:text-zinc-400">{t('The GPS team set this day.')}</p>
              </div>
            </div>
          ) : grid && deadline ? (
            <DayGrid
              start={start}
              deadline={deadline}
              today={today}
              value={form.day}
              onPick={(day) => dispatch({ type: 'PICK_DAY', day })}
            />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                {pills.map((offset) => {
                  const day = addDays(today, offset);
                  const on = !form.otherDay && form.day === day;
                  return (
                    <button
                      key={offset}
                      type="button"
                      aria-pressed={on}
                      disabled={deadlinePassed}
                      onClick={() => dispatch({ type: 'PICK_DAY', day })}
                      className={cn(PILL, on ? PICKED : PILL_OFF)}
                    >
                      {pillLabel(t, offset)}
                    </button>
                  );
                })}
                <button
                  type="button"
                  aria-pressed={form.otherDay}
                  disabled={deadlinePassed}
                  onClick={() => dispatch({ type: 'OTHER_DAY' })}
                  className={cn(PILL, form.otherDay ? PICKED : PILL_OFF)}
                >
                  {t('Another day')}
                </button>
              </div>
              {form.otherDay && (
                <Input
                  type="date"
                  aria-label={t('Another day')}
                  min={start}
                  max={deadline ?? undefined}
                  value={form.day}
                  onChange={(event) => dispatch({ type: 'SET_DAY', day: event.target.value })}
                  className="h-11 rounded-md text-[15px]"
                />
              )}
            </>
          )}
          {load > 0 && (
            <p className="text-[12.5px] text-zinc-500 dark:text-zinc-400">
              {t('You have already accepted {{count}} intervention that day', { count: load })}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="accept-time" className={LABEL}>
            {t('Time of the visit')}
          </label>
          <Input
            id="accept-time"
            type="time"
            value={form.time}
            disabled={deadlinePassed}
            onChange={(event) => dispatch({ type: 'SET_TIME', time: event.target.value })}
            className="h-11 rounded-md text-[15px]"
          />
          {label && hours && (
            <p className="text-[12.5px] text-zinc-500 dark:text-zinc-400">
              {t('Preferred: {{window}} ({{hours}})', { window: label, hours })}
            </p>
          )}
          {isOutsideWindow(request.timeWindow, form.time) && (
            <p className="text-[12.5px] text-amber-700 dark:text-amber-400">
              {t('Outside the requested window — the GPS team will be informed.')}
            </p>
          )}
        </div>

        {(refusal || error) && (
          <p className="text-[13px] leading-snug text-rose-600 dark:text-rose-400" role="alert">
            {refusal ?? error}
          </p>
        )}
      </div>

      <div className="flex shrink-0 flex-col gap-3 border-t border-zinc-200 px-5 pb-8 pt-4 dark:border-zinc-800">
        {tariffText && (
          <p className="text-[13px] font-medium text-zinc-900 dark:text-zinc-100">
            {t('Tariff offered: {{amount}}', { amount: tariffText })}
          </p>
        )}
        {feeText && (
          <p className="-mt-2 text-[13px] text-zinc-500 dark:text-zinc-400">
            {t('Travel fee: {{amount}}', { amount: feeText })}
          </p>
        )}
        <div className="flex gap-2.5">
          {taken ? (
            <Button type="button" className="h-11 flex-1" onClick={onBackToList}>
              {t('Back to my requests')}
            </Button>
          ) : (
            <>
              <Button type="button" variant="outline" className="h-11 px-5" onClick={onClose}>
                {t('Cancel')}
              </Button>
              {/* Wraps at 360 px: the dated label is wider than the footer leaves it (CA12). */}
              <Button
                type="button"
                className="h-auto min-h-11 min-w-0 flex-1 whitespace-normal py-2"
                disabled={!canConfirm}
                onClick={submit}
              >
                {confirmLabel()}
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

interface AcceptSheetProps extends AcceptFormProps {
  open: boolean;
  /** Bumped on each opening: a fresh form, while the Drawer stays mounted to animate its close. */
  session: number;
}

const AcceptSheet = ({ open, session, ...form }: AcceptSheetProps) => {
  const { t } = useTranslation();
  return (
    <Drawer open={open} onOpenChange={(next) => !next && form.onClose()}>
      <DrawerContent className="mx-auto max-h-[92dvh] w-full overflow-hidden sm:max-w-lg">
        <DrawerHeader className="shrink-0 text-left">
          <DrawerTitle>{t('Accept the request')}</DrawerTitle>
          <DrawerDescription>{t('Choose the day and time of your visit.')}</DrawerDescription>
        </DrawerHeader>
        <AcceptForm key={session} {...form} />
      </DrawerContent>
    </Drawer>
  );
};

export default AcceptSheet;
